import React, { useEffect, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Card, PageHeader, Button, Spinner, Badge } from "@/components/ui";
import { Download, FileText, Lock, Share2, ShieldAlert, Check, X, UserCircle2, Megaphone, Globe, Link2, ExternalLink, PlayCircle, FolderDown, Inbox, Copy } from "lucide-react";
import { publicOrigin } from "@/lib/publicUrl";

const MD_URL = "/docs/GoMCO-GTM-Playbook.md";

const DOCS = [
  { name: "Founder & Eagle Credibility one-pager", cls: "external", note: "Turns 'new company' into your #1 asset — lead with it", pdf: "founder-credibility", file: "GoMCO-Founder-Eagle-Credibility.pdf" },
  { name: "RFP Response", cls: "external", note: "The submission — your primary deliverable", pdf: "rfp-response", file: "GoMCO-Trillium-RFP-Response.pdf" },
  { name: "Product Tour + screenshots", cls: "external", note: "Lead with it — proves a real platform", pdf: "rfp-response", file: "GoMCO-Trillium-RFP-Response.pdf" },
  { name: "Delivery Approach (sanitized)", cls: "external", note: "Turnkey/staffing narrative only", pdf: "delivery-approach-external", file: "GoMCO-Delivery-Approach-External.pdf" },
  { name: "Pricing grid (at BAFO)", cls: "external", note: "The numbers only — never the margin behind them", pdf: "pricing-grid", file: "GoMCO-Trillium-Pricing-Grid.pdf" },
  { name: "Security & compliance attestations", cls: "external", note: "Under NDA at BAFO/contract", pdf: "security-attestations", file: "GoMCO-Security-Compliance-Attestations.pdf" },
  { name: "Delivery P&L / margins / calculator", cls: "internal", note: "Reveals your economics — never show", pdf: "delivery-model", file: "GoMCO-Trillium-Delivery-Model.pdf" },
  { name: "AI-Augmented Delivery (reframed)", cls: "external", note: "The submit-ready AI story — no FTE/cost/margin", pdf: "ai-augmented-external", file: "GoMCO-AI-Augmented-Delivery-External.pdf" },
  { name: "AI-Operated Lean Model (internal)", cls: "internal", note: "Full FTE/cost/margin — planning only, never share", pdf: "ai-operated-model", file: "GoMCO-AI-Operated-Model.pdf" },
  { name: "Monetization Model", cls: "internal", note: "Pricing tactics, upsells, margins", pdf: "monetization", file: "GoMCO-Monetization-Model.pdf" },
  { name: "MCO Expansion Playbook", cls: "internal", note: "Shows Trillium is one of many targets", pdf: "mco-playbook", file: "GoMCO-MCO-Expansion-Playbook.pdf" },
  { name: "Sales & Operator Playbook", cls: "internal", note: "Sales strategy", pdf: "sales-playbook", file: "GoMCO-Sales-and-Operator-Playbook.pdf" },
  { name: "Trillium Approach & Credibility", cls: "internal", note: "Your win strategy against them", pdf: "trillium-approach", file: "GoMCO-Trillium-Approach.pdf" },
  { name: "This GTM Playbook", cls: "internal", note: "The master strategy — never share", pdf: "gtm-playbook", file: "GoMCO-GTM-Playbook.pdf" },
  { name: "Meeting Prep Cheat-Sheets", cls: "internal", note: "Per-meeting talking points + Q&A war room", pdf: "meeting-prep", file: "GoMCO-Meeting-Prep-Cheatsheets.pdf" },
  { name: "Staffing & AI Stack Build-Out", cls: "internal", note: "Who to hire, pay, sourcing + which AI to use", pdf: "staffing-ai-stack", file: "GoMCO-Staffing-and-AI-Stack.pdf" },
];

const CLS_META = {
  external: { label: "SHARE", icon: Share2, badge: "Active", ring: "border-indigo-200 bg-indigo-50" },
  internal: { label: "INTERNAL", icon: Lock, badge: "Denied", ring: "border-red-200 bg-red-50" },
  reframe: { label: "REFRAME", icon: ShieldAlert, badge: "Pending", ring: "border-amber-200 bg-amber-50" },
};

const SAY = [
  "I'm personally accountable — you'll have my direct line.",
  "We're a lean, AI-native team that scales on award.",
  "AI accelerates the work; licensed humans stay accountable.",
  "Hold us to structure: escrow, milestones, SLA penalties.",
  "You own your data via an open, versioned API — no lock-in.",
  "No legacy overhead, so we pass modern economics to you.",
];
const DONT = [
  "\"I'm the only employee\" / \"We have no staff.\"",
  "Any margin, cost-to-serve, or FTE economics.",
  "\"The AI runs everything / makes clinical decisions.\"",
  "Guaranteed savings, ROI, or HEDIS outcomes.",
  "Bad-mouthing incumbents by name.",
  "That Trillium is one of many targets.",
];

async function downloadPdf() {
  try {
    const res = await api.get("/docs/gtm-playbook/pdf", { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url; a.download = "GoMCO-GTM-Playbook.pdf"; a.click();
    URL.revokeObjectURL(url);
  } catch { toast.error("PDF export failed"); }
}

async function downloadDocPdf(pdfKey, fileName) {
  try {
    const res = await api.get(`/docs/${pdfKey}/pdf`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url; a.download = fileName; a.click();
    URL.revokeObjectURL(url);
  } catch { toast.error("PDF export failed"); }
}

async function downloadShareableBundle() {
  try {
    const res = await api.get("/docs/shareable-bundle/zip", { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url; a.download = "GoMCO-Trillium-Shareable-Packet.zip"; a.click();
    URL.revokeObjectURL(url);
  } catch { toast.error("Bundle export failed"); }
}

function DistributionCard() {
  const [copied, setCopied] = React.useState("");
  const origin = publicOrigin();
  const links = [
    { key: "showcase", label: "Public Showcase Page", url: `${origin}/showcase`, desc: "Share the concept — multiple explainer videos, no documents needed. Safe for anyone." },
    { key: "demo", label: "Live Demo (send to Trillium with the RFP)", url: `${origin}/demo`, desc: "A live, care-manager view of the real platform — internal Business tabs are hidden." },
  ];
  const copy = (k, url) => { try { navigator.clipboard?.writeText(url)?.catch(() => {}); } catch { /* noop */ } setCopied(k); toast.success("Link copied"); setTimeout(() => setCopied(""), 1600); };
  const downloadSubmittal = async () => {
    try {
      const res = await api.get("/docs/submittal-packet/zip", { responseType: "blob" });
      const u = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = u; a.download = "GoMCO-Trillium-RFP-Submittal-Packet.zip"; a.click();
      URL.revokeObjectURL(u);
    } catch { toast.error("Download failed"); }
  };
  return (
    <Card className="p-6 mb-6 bg-brand-950 text-white border-0" data-testid="distribution-card">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="font-display text-lg font-bold">Distribution & Submission</h2>
          <p className="text-sm text-brand-100">Everything you send to Trillium — the submittal packet plus your shareable links.</p>
        </div>
        <Button variant="subtle" onClick={downloadSubmittal} data-testid="download-submittal-packet">
          <FolderDown className="h-4 w-4" /> Download Submittal-Ready Packet (.zip)
        </Button>
      </div>
      <div className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-3">
        {links.map((l) => (
          <div key={l.key} className="rounded-md bg-white/5 border border-white/10 p-3" data-testid={`dist-${l.key}`}>
            <div className="flex items-center gap-2">
              {l.key === "showcase" ? <Globe className="h-4 w-4 text-brand-300" /> : <PlayCircle className="h-4 w-4 text-brand-300" />}
              <p className="font-display font-semibold text-sm">{l.label}</p>
            </div>
            <p className="text-xs text-brand-100 mt-1">{l.desc}</p>
            <code className="mt-2 block max-w-full truncate rounded bg-black/30 px-2 py-1 text-[11px] text-brand-100" data-testid={`dist-url-${l.key}`}>{l.url}</code>
            <div className="flex gap-2 mt-2">
              <Button size="sm" variant="subtle" onClick={() => copy(l.key, l.url)} data-testid={`copy-${l.key}`}>{copied === l.key ? <Check className="h-3.5 w-3.5" /> : <Link2 className="h-3.5 w-3.5" />} {copied === l.key ? "Copied" : "Copy"}</Button>
              <a href={l.url} target="_blank" rel="noopener noreferrer"><Button size="sm"><ExternalLink className="h-3.5 w-3.5" /> Open</Button></a>
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}

function LeadsInbox() {
  const [leads, setLeads] = React.useState(null);
  React.useEffect(() => { api.get("/leads").then((r) => setLeads(r.data)).catch(() => setLeads([])); }, []);
  return (
    <Card className="p-6 mb-6" data-testid="leads-inbox">
      <div className="flex items-center gap-2 mb-1"><Inbox className="h-5 w-5 text-brand-700" /><h2 className="font-display text-xl font-bold">Prospect Leads</h2></div>
      <p className="text-sm text-muted-foreground mb-4">Warm leads captured from the public Showcase page — great for expanding to other MCOs.</p>
      {leads === null ? <Spinner label="Loading leads…" /> : leads.length === 0 ? (
        <p className="text-sm text-muted-foreground" data-testid="leads-empty">No leads yet. Share your Showcase link to start capturing interest.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
              <th className="px-3 py-2 font-semibold">Name</th><th className="px-3 py-2 font-semibold">Organization</th><th className="px-3 py-2 font-semibold">Email</th><th className="px-3 py-2 font-semibold">When</th></tr></thead>
            <tbody className="divide-y divide-border">
              {leads.map((l) => (
                <tr key={l.id} className="hover:bg-muted transition-colors" data-testid="lead-row">
                  <td className="px-3 py-2 font-medium">{l.name}</td>
                  <td className="px-3 py-2 text-muted-foreground">{l.org || "—"}</td>
                  <td className="px-3 py-2 text-muted-foreground">{l.email}</td>
                  <td className="px-3 py-2 text-muted-foreground text-xs">{new Date(l.created_at).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

function McoDuplicateCard() {
  const [busy, setBusy] = React.useState("");
  const MCOS = [
    { key: "alliance", name: "Alliance Health", hq: "Morrisville, NC" },
    { key: "partners", name: "Partners Health Management", hq: "Gastonia, NC" },
    { key: "vaya", name: "Vaya Health", hq: "Asheville, NC" },
  ];
  const dl = async (mco, name) => {
    setBusy(mco);
    try {
      const res = await api.get(`/docs/mco-packet/${mco}/zip`, { responseType: "blob" });
      const u = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = u; a.download = `GoMCO-${name.split(" ")[0]}-RFP-Packet-DRAFT.zip`; a.click();
      URL.revokeObjectURL(u);
      toast.success(`${name} packet generated — review the DRAFT-README first`);
    } catch { toast.error("Generation failed"); }
    setBusy("");
  };
  return (
    <Card className="p-6 mb-6" data-testid="mco-duplicate-card">
      <div className="flex items-center gap-2 mb-1"><Copy className="h-5 w-5 text-brand-700" /><h2 className="font-display text-xl font-bold">Duplicate for another MCO</h2></div>
      <p className="text-sm text-muted-foreground mb-4">If Trillium doesn't land, fire off a tailored bid in minutes. This clones the submission-safe packet re-branded for another NC LME/MCO. <strong className="text-foreground">DRAFT</strong> — verify membership, counties &amp; RFP specifics before sending (see the DRAFT-README inside).</p>
      <div className="grid sm:grid-cols-3 gap-3">
        {MCOS.map((m) => (
          <div key={m.key} className="rounded-md border border-border p-4 flex flex-col" data-testid={`mco-${m.key}`}>
            <p className="font-display font-semibold text-sm">{m.name}</p>
            <p className="text-xs text-muted-foreground mb-3">{m.hq}</p>
            <Button size="sm" className="mt-auto" disabled={busy === m.key} onClick={() => dl(m.key, m.name)} data-testid={`mco-download-${m.key}`}>
              <FolderDown className="h-4 w-4" /> {busy === m.key ? "Generating…" : "Generate packet"}
            </Button>
          </div>
        ))}
      </div>
    </Card>
  );
}

export default function GoToMarket() {
  const [md, setMd] = useState(null);
  useEffect(() => { fetch(MD_URL).then((r) => r.text()).then(setMd).catch(() => setMd("Failed to load.")); }, []);

  return (
    <div className="vc-fade-up max-w-5xl">
      <PageHeader
        title="Turnkey AI Go-To-Market"
        subtitle="The complete Trillium win strategy — what to say, what to share, and how to position yourself as CEO. Internal use only."
        actions={<div className="flex gap-2">
          <Button variant="outline" onClick={downloadPdf} data-testid="gtm-playbook-pdf"><FileText className="h-4 w-4" /> PDF</Button>
          <a href={MD_URL} download><Button data-testid="gtm-playbook-md"><Download className="h-4 w-4" /> Markdown</Button></a>
        </div>}
      />

      <div className="mb-6 rounded-md bg-red-50 border border-red-200 px-4 py-2.5 flex items-center gap-2 text-sm text-red-700" data-testid="gtm-confidential">
        <Lock className="h-4 w-4" /> <strong>Confidential — Internal.</strong> This playbook and everything marked INTERNAL must never be shared with Trillium.
      </div>

      <DistributionCard />

      {/* Document classification board */}
      <Card className="p-6 mb-6" data-testid="gtm-classification">
        <div className="flex flex-wrap items-start justify-between gap-3 mb-1">
          <h2 className="font-display text-xl font-bold">Document Classification</h2>
          <Button variant="outline" onClick={downloadShareableBundle} data-testid="download-shareable-bundle">
            <Download className="h-4 w-4" /> Download all shareable docs (.zip)
          </Button>
        </div>
        <p className="text-sm text-muted-foreground mb-4">Share what proves capability and transfers risk. Keep anything revealing cost, margins, headcount, or strategy.</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {DOCS.map((d) => {
            const meta = CLS_META[d.cls];
            return (
              <div key={d.name} className={`rounded-md border p-3 ${meta.ring}`} data-testid={`doc-${d.cls}`}>
                <div className="flex items-center justify-between gap-2">
                  <span className="font-semibold text-sm text-foreground">{d.name}</span>
                  <span className="flex items-center gap-1 text-[10px] font-bold tracking-wider shrink-0">
                    <meta.icon className="h-3.5 w-3.5" /> {meta.label}
                  </span>
                </div>
                <p className="text-xs text-muted-foreground mt-1">{d.note}</p>
                {d.pdf && (
                  <button
                    onClick={() => downloadDocPdf(d.pdf, d.file)}
                    data-testid={`doc-pdf-${d.pdf}`}
                    className="mt-2 inline-flex items-center gap-1 text-[11px] font-semibold text-brand-700 hover:text-brand-900 hover:underline transition-colors"
                  >
                    <FileText className="h-3.5 w-3.5" /> Download PDF
                  </button>
                )}
              </div>
            );
          })}
        </div>
        <div className="flex flex-wrap gap-4 mt-4 text-xs text-muted-foreground">
          <span className="flex items-center gap-1"><Share2 className="h-3.5 w-3.5 text-indigo-600" /> Share with Trillium</span>
          <span className="flex items-center gap-1"><ShieldAlert className="h-3.5 w-3.5 text-amber-600" /> Reframe only — never the raw math</span>
          <span className="flex items-center gap-1"><Lock className="h-3.5 w-3.5 text-red-600" /> Internal — never share</span>
        </div>
      </Card>

      {/* CEO positioning */}
      <McoDuplicateCard />
      <LeadsInbox />

      <Card className="p-6 mb-6 bg-brand-950 text-white border-0" data-testid="gtm-ceo">
        <div className="flex items-center gap-3 mb-3">
          <UserCircle2 className="h-8 w-8 text-brand-300" />
          <div>
            <h2 className="font-display text-lg font-bold">CEO Positioning — Omeze Nwankwo, Founder &amp; CEO</h2>
            <p className="text-xs text-brand-100">The visionary founder who personally guarantees Trillium's success.</p>
          </div>
        </div>
        <p className="text-sm text-brand-100">
          Position: single point of executive accountability, chief architect of the vision, accessible and backed by named partners and hires.
          Posture: calm, senior, curious — ask more than you pitch. Concede what's true ("yes, we're early-stage") and immediately pivot to the mitigation
          (escrow, milestone payments, paid pilot). <strong className="text-white">Confidence without bravado.</strong>
        </p>
      </Card>

      {/* Say / Don't say */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        <Card className="p-5" data-testid="gtm-say">
          <div className="flex items-center gap-2 mb-3"><Megaphone className="h-5 w-5 text-indigo-600" /><h3 className="font-display font-semibold">Say This</h3></div>
          <ul className="space-y-2">
            {SAY.map((s, i) => <li key={i} className="flex items-start gap-2 text-sm"><Check className="h-4 w-4 text-indigo-600 mt-0.5 shrink-0" /><span className="text-foreground">{s}</span></li>)}
          </ul>
        </Card>
        <Card className="p-5" data-testid="gtm-dont">
          <div className="flex items-center gap-2 mb-3"><X className="h-5 w-5 text-red-600" /><h3 className="font-display font-semibold">Never Say This</h3></div>
          <ul className="space-y-2">
            {DONT.map((s, i) => <li key={i} className="flex items-start gap-2 text-sm"><X className="h-4 w-4 text-red-600 mt-0.5 shrink-0" /><span className="text-foreground">{s}</span></li>)}
          </ul>
        </Card>
      </div>

      <Card className="p-6 sm:p-10">
        {md === null ? <Spinner label="Loading playbook…" /> : (
          <div className="rfp-prose" data-testid="gtm-content">
            <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]}>{md}</ReactMarkdown>
          </div>
        )}
      </Card>
    </div>
  );
}
